import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import cn from "classnames";
import { useSearchParams } from "react-router-dom";

// components
import { FaArrowLeft } from "react-icons/fa";
import { SBATag, Button } from "components/LibraryComponents/Components";
import DisplayValue from "components/BusinessProfileSections/DisplayValue";
import { Loader, Spinner } from "components/icons";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import { Alert } from "@trussworks/react-uswds";
import { ExportPDFIcon } from "components/icons";
// sections
import SectionNavigationMenu from "components/BusinessProfileSections/SectionNavigationMenu/SectionNavigationMenu";
import ProfileOverviewSection from "components/BusinessProfileSections/ProfileOverviewSection/ProfileOverviewSection";
import OrganizationOwnershipSection from "components/BusinessProfileSections/OrganizationOwnershipSection/OrganizationOwnershipSection";
import SizeOverviewSection from "components/BusinessProfileSections/SizeOverviewSection/SizeOverviewSection";
import ProfileCertifications from "components/BusinessProfileSections/ProfileCertifications/ProfileCertifications";
import NaicsCodesSection from "components/BusinessProfileSections/NaicsCodesSection/NaicsCodesSection";
import ServiceInformationSection from "components/BusinessProfileSections/ServiceInformationSection/ServiceInformationSection";
import PerformanceHistorySection from "components/BusinessProfileSections/PerformanceHistorySection/PerformanceHistorySection";
import ExportProfileSection from "components/BusinessProfileSections/ExportProfileSection/ExportProfileSection";

// store
import { useSearchStore } from "store/searchStore";
import { useResponsiveStore } from "store/useResponsiveStore";
import useAuthStore from "store/useAuthStore";

// constants
import { Permissions } from "constants/permissions";

// query
import { useProfileQuery } from "./query";

// styles
import styles from "./BusinessProfileDetails.module.scss";

// utils
import { formatTimestamp } from "utils/helper";

export default function BusinessProfileDetails() {
  const navigate = useNavigate();
  const { protocol, host, pathname } = window.location;
  const urlClean = `${protocol}//${host}${pathname}`;
  const { uei, cage_code } = useParams();
  const [searchParams] = useSearchParams();

  const { setProfile, business_profile } = useSearchStore();
  const { setMenuComponent, device } = useResponsiveStore();
  const { user, isAuthenticated } = useAuthStore();

  const role = user?.role ?? "guest"
  const privileged = role ? Permissions[role]?.privilegedSearch : false;
  const canManageBusiness = isAuthenticated && role === "admin";

  const { data, isPending, errorType, dataUpdatedAt } = useProfileQuery(uei, cage_code);

  useEffect(() => {
    if (data?.entity) {
      setProfile(data.entity);
    }
  }, [data]);

  useEffect(() => {
    setMenuComponent(<SectionNavigationMenu />);
    return () => {
      setMenuComponent(null);
    };
  }, []);

  const handleBackTo = () => {
    const returnTo = searchParams.get("page");

    navigate("/advanced?page=" + returnTo);
  };

  const handlePrintToPDF = async () => {
    window.print()
  };

  const navigateToEditProfilePage = () => {
    navigate(`/profile/${business_profile?.uei}/${business_profile?.cage_code}/edit`);
  }


  if (isPending || (!isPending && !errorType && !business_profile)) {
    // if is pending, or store is not yet updated
    return (
      <>
        <Helmet>
          <title>Loading...</title>
        </Helmet>
        <div className={styles["loader-root"]}>
          <Loader />
        </div>
      </>
    );
  }

  if (!isPending && errorType) {
    return (
      <>
        <Helmet>
          <title>Page not found</title>
        </Helmet>
        <NotFoundPage hideLayout />
      </>
    );
  }

  const currentCertificates = business_profile.certs.filter(x => x.active && !x?.pending);
  const pendingCertifications = business_profile.certs.filter(x => x?.pending);
  const pastCertificates = business_profile.certs.filter(x => !x.active && !x?.suspended && !x?.pending);
  const suspendedCertificates = business_profile.certs.filter(x => x?.suspended);

  const showLimitedProfile = business_profile?.public_display_limited && !privileged;


  const isCertEmpty =
    currentCertificates.length === 0 &&
    pendingCertifications.length === 0 &&
    pastCertificates.length === 0 &&
    suspendedCertificates.length === 0;

  const metaTitle = business_profile?.legal_business_name
    ? `${business_profile.legal_business_name} | SBA Small Business Search`
    : "SBA Small Business Search";

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
      </Helmet>

      <div className={styles["business-profile-details-root"]}>
        <button className={styles["back-btn"]} onClick={handleBackTo}>
          <FaArrowLeft
            color="#005ea2"
            aria-label="Back to advanced search page"
          />
          <span>Back to results</span>
        </button>


        <div className={styles["basic-details-card"]}>
          <div className={styles["left"]}>
            <div className={styles["print-meta"]}>
              <p>{urlClean}</p>
              <p>{formatTimestamp(dataUpdatedAt)}</p>
            </div>
            <h1 className={styles["title"]}>
              {business_profile?.legal_business_name}
            </h1>
            <p className={styles["timestamp"]}>{formatTimestamp(dataUpdatedAt)}</p>

            <div className={cn(styles["group"], styles["mt-2"])}>
              <h5 className={cn(styles["label"], styles["bold"])}>UEI:</h5>
              <DisplayValue
                messageElement="p"
                className={styles["value"]}
                value={business_profile?.uei}
                message="Unknown"
              />
            </div>
            <div className={styles["group"]}>
              <h5 className={cn(styles["label"], styles["bold"])}>CAGE code:</h5>
              <DisplayValue
                messageElement="p"
                value={business_profile?.cage_code}
                className={styles["value"]}
                message="Unknown"
              />
            </div>

            {isCertEmpty && (
              <p className={styles["no-cert"]}>
                This firm does not have any active or former SBA certifications
              </p>
            )}

            {/* Extra top space */}
            <div className={cn(styles["group"], styles["mt-2"])} />

            {currentCertificates.length > 0 && (
              <div className={cn(styles["group"], styles["mt-2"])}>
                <p className={styles["label"]}>Current SBA certifications:</p>
                <span className={styles["value"]}>
                  {currentCertificates.map(cert => (
                    <SBATag
                      key={cert.name}
                      name={cert.name}
                      isPending={cert.pending}
                    />
                  ))}
                </span>
              </div>
            )}

            {pendingCertifications.length > 0 && (
              <div className={cn(styles["group"], styles["mt-2"])}>
                <p className={styles["label"]}>Pending SBA certifications:</p>
                <span className={styles["value"]}>
                  {pendingCertifications.map(cert => (
                    <SBATag
                      key={cert.name}
                      name={cert.name}
                      isPending={cert.pending}
                    />
                  ))}
                </span>
              </div>
            )}

            {suspendedCertificates.length > 0 && (
              <div className={cn(styles["group"], styles["mt-2"])}>
                <p className={styles["label"]}>Suspended SBA certifications:</p>
                <span className={styles["value"]}>
                  {suspendedCertificates.map((item) => (
                    <SBATag
                      key={item?.name}
                      name={item?.name}
                      isPending={item?.pending}
                      background="#FFBE2E"
                      isSuspended
                    />
                  ))}
                </span>
              </div>
            )}

            {pastCertificates.length > 0 && (
              <div className={cn(styles["group"], styles["mt-2"])}>
                <p className={styles["label"]}>Previous SBA certifications:</p>
                <span className={styles["value"]}>
                  {pastCertificates.map((item) => (
                    <SBATag
                      key={item.name}
                      name={item.name}
                      isPending={item.pending}
                      background="#565C65"
                    />
                  ))}
                </span>
              </div>
            )}
          </div>
          <div className={styles["right"]}>
            <Button
              className={styles["export-btn"]}
              onClick={handlePrintToPDF}
              type="button"
            >
              <ExportPDFIcon /> <span>Export as .pdf</span>
            </Button>
            {canManageBusiness && (
              <Button
                className={styles["manage-btn"]}
                onClick={navigateToEditProfilePage}
                type="button"
              >
                Manage business
              </Button>
            )}
          </div>
        </div>

        <div
          className={cn(
            styles["navigation-content"],
            "usa-in-page-nav-container"
          )}
        >
          {device === "desktop" && (
            <SectionNavigationMenu isLimitedProfilePage={showLimitedProfile} />
          )}

          <div id="main-content" className={cn(styles["content"])}>
            {!showLimitedProfile && (
              <>
                {business_profile?.public_display_limited && (
                  <Alert
                    type="info"
                    headingLevel="h6"
                    className={styles["alert-wrapper"]}
                  >
                    This business has opted out of listing additional
                    information publicly on Small Business Search, but you have
                    permission to view their SAM.data.
                  </Alert>
                )}
                <ProfileOverviewSection isPrivileged={privileged} />
                <OrganizationOwnershipSection />
                {privileged && <SizeOverviewSection />}
                <ProfileCertifications />
                <NaicsCodesSection naics={data?.naics} businessProfileData={data?.entity} />
                <ServiceInformationSection />
                <PerformanceHistorySection
                  performance_history={data?.performanceHistory}
                />
                <ExportProfileSection />
              </>
            )}

            {showLimitedProfile && (
              <>
                <Alert
                  type="info"
                  headingLevel="h6"
                  className={styles["alert-wrapper"]}
                >
                  This business has opted out of listing additional information
                  on Small Business Search. If you are the business owner and
                  want to change this, log in to{" "}
                  <a
                    href="https://secure.login.gov"
                    target="_blank"
                    rel="noreferrer"
                  >
                    SAM.gov
                  </a>{" "}
                  and update your “Public display” preferences.
                </Alert>

                <ProfileCertifications />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
